import type { PrintParams } from '@ahd/shared/types/ahd-cars-print/params';
import useTheme from '@mui/material/styles/useTheme';
import { type GridDensity } from '@mui/x-data-grid-pro';
import type { AxiosError } from 'axios';
import { startOfDay } from 'date-fns/startOfDay';
import { sub } from 'date-fns/sub';
import { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type Location, useLocation, useNavigate, useRoutes } from 'react-router-dom';
import pjson from '../../package.json';
import { setCarList, setCarListFilter, setCarListSize, setImageSettings, setLoginToken, setUpdateRequired, setUserSettings } from '../_flux/Actions';
import { type FluxStore } from '../_flux/Store';
import sEyecatcher from '../_json/s_eyecatcher';
import sSpaltenFahrzeugliste from '../_json/s_spaltenFahrzeugliste';
import sSpaltenFahrzeuglisteOld from '../_json/s_spaltenFahrzeuglisteOld';
import sNewStandardTableLayouts from '../_json/s_standardTableLayouts';
import { getCarList } from '../_tools/AxiosCar';
import { getFirma } from '../_tools/AxiosFirma';
import { getBildersettings, urlImagesRelease } from '../_tools/AxiosVars';
import { formatTimestamp } from '../_tools/Date';
import DialogBox from '../_tools/DialogBox';
import Loading from '../_tools/Loading';
import {
    clearSession,
    getAllPortals,
    getBtoB,
    getConf,
    getIdFirma,
    getIdStandort,
    getRoute,
    getSession,
    isBtoB,
    setConf,
    setPrintFirma,
    setVertretungSeit,
    setVertretungUser,
} from '../_tools/Session';
import { getAllFieldFromArray, getElementFromArray } from '../_tools/Texte';
import Toast from '../_tools/Toast';
import { betterUseNavigate, checkUndefinedNullOrEmpty, checkUndefinedOrNull, isLocalServer, neededTableColumnDefinitionForUserSettings } from '../_tools/Tools';
import { type Vertrage, getVertrag } from '../_tools/Vertrag';
import { type DataGridEnrichedColDef, type DataGridProps } from '../_tools/datagrid/types';
import { routesAhdCars } from '../paths';
import { type CustomTheme } from '../provider/ThemeProvider';
import { LEAD } from '../vars';

let updateTimer;
let retryTimer;
let carLoadAttempts = 0;

let controllerBilder;
let controllerFirma;
let controllerCars;
let controllerCarsSold;

export type UserSettings = {
    main: {
        darkMode: boolean;
        sounds: boolean;
        hiddenBubbles: string[];
        ignoreNewsVersion: string;
    };
    dashboard: ('vehicleUbersicht' | 'vehicleStandzeit' | 'vehicleStandzeitEins' | 'leadUbersicht' | 'leadReaktion' | 'leadPortale' | 'leadStatusNeu' | 'leadStatusBeendet' | 'leadMitarbeiter')[];
    fahrzeugliste: {
        filterAktTypList: number[] | undefined;
        filterFirma: number | undefined;
        filterHideVirtuell: boolean | undefined;
        listenansicht: boolean;
        tableDensity: GridDensity;
        tableMode: number;
        tableModeVerkauft: number;
        tableLayouts: {
            [string: number]: DataGridProps['columns'];
        };
    };
    fahrzeugeinzelansicht: {
        print: Pick<PrintParams, 'ausZuordnung' | 'netto' | 'vka'>;
        statistik: {
            showErfassung: boolean;
            showRechnung: boolean;
        };
        verkauf: {
            aufmerksam: number;
        };
    };
    reset: boolean;
};

export const defaultConfig: (theme: CustomTheme, vertrage: Vertrage) => UserSettings = (theme, vertrage) => ({
    reset: false,
    main: {
        darkMode: false,
        sounds: true,
        hiddenBubbles: [],
        ignoreNewsVersion: undefined,
    },
    dashboard: !LEAD
        ? []
        : getVertrag('lead', vertrage)
          ? ['vehicleUbersicht', 'vehicleStandzeit', 'vehicleStandzeitEins', 'leadUbersicht', 'leadReaktion']
          : ['vehicleUbersicht', 'vehicleStandzeit', 'vehicleStandzeitEins'],
    fahrzeugliste: {
        filterAktTypList: undefined,
        filterFirma: undefined,
        filterHideVirtuell: true,
        listenansicht: false,
        tableDensity: 'standard',
        tableMode: 0,
        tableModeVerkauft: 6,
        tableLayouts: {
            0: sNewStandardTableLayouts(0, theme, vertrage),
            1: sNewStandardTableLayouts(1, theme, vertrage), // Effizienz
            2: sNewStandardTableLayouts(2, theme, vertrage), // Finanzierung
            4: sNewStandardTableLayouts(4, theme, vertrage), // mobile.de
            5: sNewStandardTableLayouts(5, theme, vertrage), // Leads
            6: sNewStandardTableLayouts(6, theme, vertrage), // Verkauft
            7: sNewStandardTableLayouts(7, theme, vertrage), // Standzeit Eins
            8: sNewStandardTableLayouts(8, theme, vertrage), // Fahrzeugexport
            10: sNewStandardTableLayouts(10, theme, vertrage), // Tracking
        },
    },
    fahrzeugeinzelansicht: {
        print: {
            vka: true,
            netto: false,
            ausZuordnung: false,
        },
        statistik: {
            showErfassung: true,
            showRechnung: false,
        },
        verkauf: {
            aufmerksam: getIdFirma() ? 8 : 12,
        },
    },
});

const EnsureLoggedInContainer = () => {
    const loginToken = useSelector((state: FluxStore) => state.loginToken);
    const updateRequired = useSelector((state: FluxStore) => state.updateRequired);
    const userSettings = useSelector((state: FluxStore) => state.userSettings);
    const vertrage = useSelector((state: FluxStore) => state.vertrage);
    const dispatch = useDispatch();

    const loc: Location & { state: { [index: string]: string | number | boolean } } = useLocation();
    const navigate = useNavigate();
    const theme: CustomTheme = useTheme();

    const startUpdateChecker = () => {
        try {
            fetch('/version.js?ts=' + formatTimestamp(new Date()))
                .then((response) => {
                    response.text().then((versionjs) => {
                        const versionpjson = pjson.version.replace(/(\r\n|\n|\r|\s)/gm, '');
                        const text = versionjs.includes('Error') ? undefined : versionjs.replace(/(\r\n|\n|\r|\s)/gm, '');
                        if (!updateRequired && !checkUndefinedNullOrEmpty(text) && versionpjson !== text) dispatch(setUpdateRequired(true));
                        else if (versionpjson === text) dispatch(setUpdateRequired(false));
                    });
                })
                .catch();

            updateTimer = setInterval(
                () => {
                    fetch('/version.js?ts=' + formatTimestamp(new Date()))
                        .then((response) => {
                            response.text().then((versionjs) => {
                                const versionpjson = pjson.version.replace(/(\r\n|\n|\r|\s)/gm, '');
                                const text = versionjs.replace(/(\r\n|\n|\r|\s)/gm, '');
                                if (!updateRequired && !checkUndefinedNullOrEmpty(text) && versionpjson !== text) dispatch(setUpdateRequired(true));
                                else if (versionpjson === text) dispatch(setUpdateRequired(false));
                            });
                        })
                        .catch();
                },
                1000 * 60 * 10
            ); // 10 Minuten
        } catch (err: any) {
            console.error(err);
        }
    };

    const convertTableLayouts = (tableLayout: string[] | DataGridEnrichedColDef[]) => {
        try {
            const tableLayoutToUse = tableLayout.filter((e) => !checkUndefinedNullOrEmpty(e) && (typeof e !== 'object' || !checkUndefinedNullOrEmpty(e.field)));
            if (!checkUndefinedNullOrEmpty(tableLayoutToUse) && typeof tableLayoutToUse[0] === 'string') {
                const newTableLayout = [];
                const cols = sSpaltenFahrzeuglisteOld(theme, vertrage);
                const colsNew: DataGridProps['columns'] = sSpaltenFahrzeugliste(theme, vertrage);
                tableLayoutToUse.map((key, i) => {
                    const colOriginal = getElementFromArray(cols, key, 'i', undefined);
                    if (!checkUndefinedNullOrEmpty(colOriginal)) {
                        const colNew = getElementFromArray<any>(colsNew, colOriginal.field, 'field', {});
                        const newCol: any = {};
                        neededTableColumnDefinitionForUserSettings(true).map((f) => (newCol[f.id] = !checkUndefinedOrNull(colNew[f.id]) ? colNew[f.id] : f.default === 'index' ? i : f.default));
                        newTableLayout.push(newCol);
                    }
                });
                return newTableLayout;
            } else {
                const portals = getAllPortals().map((portal) => 'export.' + portal);
                const newTableLayout = (tableLayoutToUse as DataGridEnrichedColDef[]).map((col) => {
                    const newCol = Object.assign({}, col);
                    if (newCol.field === 'fd.fin.ejz') {
                        newCol.field = 'fd.fin.ez';
                    } else if (newCol.field === 'exp' || newCol.field === 'export') {
                        newCol.field = 'export.exp';
                    } else if (portals.includes(newCol.field)) {
                        newCol.field = newCol.field + '.exp';
                    } else if (col.field === 'vin') {
                        newCol.field = 'ch';
                    } else if (col.field === 'leads.offen') {
                        newCol.field = 'leads.offenAmount';
                    } else if (col.field === 'leads.beendet') {
                        newCol.field = 'leads.beendetAmount';
                    }
                    return newCol;
                });
                return newTableLayout;
            }
        } catch (err: any) {
            console.error(err);
        }
    };

    const getUserSettings = (reset: boolean) => {
        try {
            const sessionSettings: UserSettings = getConf();
            const lead = getVertrag('lead', vertrage);

            const mainSettings: UserSettings['main'] = {
                darkMode: !reset && !checkUndefinedOrNull(sessionSettings?.main?.darkMode) ? sessionSettings.main.darkMode : false,
                sounds: !reset && !checkUndefinedOrNull(sessionSettings?.main?.sounds) ? sessionSettings.main.sounds : true,
                hiddenBubbles: !reset && !checkUndefinedOrNull(sessionSettings?.main?.hiddenBubbles) ? sessionSettings.main.hiddenBubbles : [],
                ignoreNewsVersion: !reset && !checkUndefinedOrNull(sessionSettings?.main?.ignoreNewsVersion) ? sessionSettings.main.ignoreNewsVersion : undefined,
            };

            const settingsDashboard: UserSettings['dashboard'] =
                !reset && !checkUndefinedOrNull(sessionSettings?.dashboard)
                    ? sessionSettings.dashboard
                    : !LEAD
                      ? []
                      : lead
                        ? ['vehicleUbersicht', 'vehicleStandzeit', 'vehicleStandzeitEins', 'leadUbersicht', 'leadReaktion']
                        : ['vehicleUbersicht', 'vehicleStandzeit', 'vehicleStandzeitEins'];

            const settingsFahrzeugliste: UserSettings['fahrzeugliste'] = {
                filterAktTypList: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugliste?.filterAktTypList) ? sessionSettings.fahrzeugliste.filterAktTypList : undefined,
                filterFirma: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugliste?.filterFirma) && isBtoB() ? sessionSettings.fahrzeugliste.filterFirma : undefined,
                filterHideVirtuell: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugliste?.filterHideVirtuell) ? sessionSettings.fahrzeugliste.filterHideVirtuell : true,
                listenansicht: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugliste?.listenansicht) ? sessionSettings.fahrzeugliste.listenansicht : false,
                tableDensity: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugliste?.tableDensity) ? sessionSettings.fahrzeugliste.tableDensity : 'standard',
                tableMode: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugliste?.tableMode) ? sessionSettings.fahrzeugliste.tableMode : 0,
                tableModeVerkauft: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugliste?.tableModeVerkauft) ? sessionSettings.fahrzeugliste.tableModeVerkauft : 6,
                tableLayouts: {
                    0:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['0'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['0'])
                            : sNewStandardTableLayouts(0, theme, vertrage),
                    1:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['1'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['1'])
                            : sNewStandardTableLayouts(1, theme, vertrage), // Effizienz
                    2:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['2'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['2'])
                            : sNewStandardTableLayouts(2, theme, vertrage), // Finanzierung
                    4:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['4'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['4'])
                            : sNewStandardTableLayouts(4, theme, vertrage), // mobile.de
                    5:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['5'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['5'])
                            : sNewStandardTableLayouts(5, theme, vertrage), // Leads
                    6:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['6'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['6'])
                            : sNewStandardTableLayouts(6, theme, vertrage), // Verkauft
                    7:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['7'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['7'])
                            : sNewStandardTableLayouts(7, theme, vertrage), // Standzeit Eins
                    8:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['8'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['8'])
                            : sNewStandardTableLayouts(8, theme, vertrage), // Fahrzeugexport
                    10:
                        !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts) && !checkUndefinedNullOrEmpty(sessionSettings?.fahrzeugliste?.tableLayouts['10'])
                            ? convertTableLayouts(sessionSettings.fahrzeugliste.tableLayouts['10'])
                            : sNewStandardTableLayouts(10, theme, vertrage), // Tracking
                },
            };

            const settingsFahrzeugeinzelansicht: UserSettings['fahrzeugeinzelansicht'] = {
                print: {
                    vka: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugeinzelansicht?.print?.vka) ? sessionSettings.fahrzeugeinzelansicht.print.vka : true,
                    netto: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugeinzelansicht?.print?.netto) ? sessionSettings.fahrzeugeinzelansicht.print.netto : false,
                    ausZuordnung: !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugeinzelansicht?.print?.ausZuordnung) ? sessionSettings.fahrzeugeinzelansicht.print.ausZuordnung : false,
                },
                statistik: {
                    showErfassung:
                        !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugeinzelansicht?.statistik?.showErfassung) ? sessionSettings.fahrzeugeinzelansicht.statistik.showErfassung : true,
                    showRechnung:
                        !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugeinzelansicht?.statistik?.showRechnung) ? sessionSettings.fahrzeugeinzelansicht.statistik.showRechnung : false,
                },
                verkauf: {
                    aufmerksam:
                        !reset && !checkUndefinedOrNull(sessionSettings?.fahrzeugeinzelansicht?.verkauf?.aufmerksam) ? sessionSettings.fahrzeugeinzelansicht.verkauf.aufmerksam : getIdFirma() ? 8 : 12,
                },
            };

            const userSettingsObj: UserSettings = {
                reset: false,
                main: mainSettings,
                dashboard: settingsDashboard,
                fahrzeugliste: settingsFahrzeugliste,
                fahrzeugeinzelansicht: settingsFahrzeugeinzelansicht,
            };

            dispatch(setUserSettings(userSettingsObj));
            setConf(userSettingsObj);
        } catch (err: any) {
            console.error(err);
        }
    };

    const getPerson4Abteilung = (kunde, strAbteilung) => {
        try {
            const lPersons = kunde?.lPersons;
            if (undefined == lPersons || lPersons.length == 0) {
                return undefined;
            }
            for (let i = 0; i < lPersons.length; i++) {
                const person = lPersons[i];
                if (person.kunde?.nname == strAbteilung) {
                    return person;
                }
            }
            return undefined;
        } catch (err: any) {
            console.error(err);
        }
    };

    const getMail4Abteilung = (kunde, strAbteilung) => {
        try {
            const person = getPerson4Abteilung(kunde, strAbteilung);
            if (undefined == person) {
                return undefined;
            }
            return person.mMail && person.mMail[1] ? person.mMail[1].adr : undefined;
        } catch (err: any) {
            console.error(err);
        }
    };

    const getFirmaFunc = () => {
        try {
            if (controllerFirma) controllerFirma.abort();
            if (controllerBilder) controllerBilder.abort();

            controllerFirma = new AbortController();
            controllerBilder = new AbortController();

            getFirma(getIdStandort(), controllerFirma.signal).then(({ data }) => {
                setPrintFirma({
                    bank: {
                        name: data?.k?.k?.lBank?.[0]?.bank || '',
                        iban: data?.k?.k?.lBank?.[0]?.iban || '',
                        bic: data?.k?.k?.lBank?.[0]?.bic || '',
                    },
                    bm: getMail4Abteilung(data?.k?.k, 'Buchhaltung'),
                    c: {
                        c0: {
                            allgHomepageCarLink: data?.c?.c0?.allgHomepageCarLink,
                            homepage: data?.c?.c0?.homepage,
                            mofrbis: data?.c?.c0?.mofrbis,
                            mofrvon: data?.c?.c0?.mofrvon,
                            sabis: data?.c?.c0?.sabis,
                            savon: data?.c?.c0?.savon,
                        },
                        srr: [],
                    },
                    d: data?.c?.d,
                    f: data?.k?.k?.mTelefon?.['3']?.nummer,
                    i: data?.k?.k?.kunde?.id,
                    ih: data?.k?.k?.kunde?.inhaber,
                    logo: `${urlImagesRelease}/s/v1/firma/${data?.k?.k?.kunde?.id}/logo.png`,
                    m: data?.k?.k?.mMail?.['1']?.adr,
                    n: data?.k?.k?.kunde?.nname,
                    o: data?.k?.k?.adresse?.plz?.ort,
                    p: data?.k?.k?.adresse?.plz?.plz,
                    rg: data?.k?.k?.kunde?.registergericht,
                    rn: data?.k?.k?.kunde?.registernummer,
                    s: `${data?.k?.k?.adresse?.adr?.strasse || ''} ${data?.k?.k?.adresse?.adr?.hnr || ''}`,
                    snr: data?.k?.k?.kunde?.snr,
                    t: data?.k?.k?.mTelefon?.['1']?.nummer,
                    ustid: data?.k?.k?.kunde?.ustid,
                });
            });
            getBildersettings(getIdFirma(), controllerBilder.signal).then(({ data }) => {
                const newData: FluxStore['imageSettings'] = Object.assign({}, data);
                newData.ec = sEyecatcher?.find((e) => e?.t?.toLowerCase() === data?.eyecatcher_type?.toLowerCase())?.i as number;
                dispatch(setImageSettings(newData));
            });
        } catch (err) {
            console.error(err);
        }
    };

    const getCars = () => {
        try {
            if (controllerCars) controllerCars.abort();
            if (controllerCarsSold) controllerCarsSold.abort();

            controllerCars = new AbortController();
            controllerCarsSold = new AbortController();

            const firmen = [];
            firmen.push(getIdFirma());
            const btob = getBtoB();
            if (!checkUndefinedNullOrEmpty(btob)) {
                btob.map((firma) => firmen.push(firma.i));
            }

            getCarList(firmen, 'all', false, { ansicht: 7 }, controllerCars.signal, { noToast: true, customToken: loginToken })
                .then(({ data: dataServ }) => {
                    let newList = [];
                    const newCars = Object.assign([], dataServ?.cars);
                    const deletedCars = Object.assign([], dataServ?.del);
                    const oldList = Object.assign([]).filter((car) => !deletedCars.includes(car.id));

                    if (!checkUndefinedNullOrEmpty(newCars)) {
                        oldList.map((car) => {
                            const newDataIndex = newCars.findIndex((newCar) => newCar?.id === car?.id);
                            if (newDataIndex > -1) {
                                newList.push(newCars[newDataIndex]);
                            } else {
                                newList.push(car);
                            }
                        });

                        const alreadyHandled = getAllFieldFromArray(oldList, 'id', []);
                        newCars
                            .filter((car) => !alreadyHandled.includes(car?.id))
                            .map((car) => {
                                newList.push(car);
                            });
                    } else {
                        newList = oldList;
                    }

                    dispatch(setCarListFilter(dataServ?.facets || {}));
                    dispatch(setCarListSize(dataServ?.size || { all: 0 }));

                    getCarList(
                        firmen,
                        'all',
                        true,
                        { ansicht: 7, SoldVon: formatTimestamp(startOfDay(sub(new Date(), { days: 7 }))), SoldBis: formatTimestamp(new Date()) },
                        controllerCarsSold.signal,
                        { noToast: true, customToken: loginToken }
                    )
                        .then(({ data: dataSold }) => {
                            let newListSold = [];
                            const newCarsSold = Object.assign([], dataSold?.cars);
                            const deletedCarsSold = Object.assign([], dataSold?.del);
                            const oldListSold = Object.assign([]).filter((car) => !deletedCarsSold.includes(car.id));

                            if (!checkUndefinedNullOrEmpty(newCarsSold)) {
                                oldListSold.map((car) => {
                                    const newDataIndex = newCarsSold.findIndex((newCar) => newCar?.id === car?.id);
                                    if (newDataIndex > -1) {
                                        newList.push(newCarsSold[newDataIndex]);
                                    } else {
                                        newList.push(car);
                                    }
                                });

                                const alreadyHandledSold = getAllFieldFromArray(oldListSold, 'id', []);
                                newCarsSold
                                    .filter((car) => !alreadyHandledSold.includes(car?.id))
                                    .map((car) => {
                                        newListSold.push(car);
                                    });
                            } else {
                                newListSold = oldListSold;
                            }

                            dispatch(setCarList([...newList, ...newListSold]));
                            clearTimeout(retryTimer);
                        })
                        .catch(() => {
                            dispatch(setCarList(newList));
                        });
                })
                .catch((error: AxiosError) => {
                    Toast.addError({
                        key: 'EnsureLoggedInContainer_getCars',
                        primary: 'Fehler beim Abrufen des Fahrzeugbestands',
                        secondary: carLoadAttempts <= 1 ? 'Bitte haben Sie einen kleinen Moment Geduld, der Vorgang wird in einer Minute wiederholt' : error?.response?.data?.toString(),
                    });
                    carLoadAttempts++;
                    dispatch(setCarListFilter({}));
                    dispatch(setCarListSize({ all: 0 }));
                    dispatch(setCarList([]));
                    if (carLoadAttempts <= 1) retryTimer = setTimeout(() => getCars(), 60000);
                });
        } catch (err: any) {
            console.error(err);
        }
    };

    useEffect(
        function () {
            if (checkUndefinedNullOrEmpty(loginToken)) {
                betterUseNavigate(navigate, loc?.state, { replace: true, pathname: '/loginr', state: { redir: getRoute(loc.pathname) } }, false);
            } else if (LEAD) {
                getCars();
                getUserSettings(false);
                getFirmaFunc();
                if (!isLocalServer()) startUpdateChecker();
                Toast.allowDesktopNotifications();
            } else {
                getUserSettings(false);
                getFirmaFunc();
                if (!isLocalServer()) startUpdateChecker();
                Toast.allowDesktopNotifications();
            }

            return function cleanup() {
                clearInterval(updateTimer);
            };
        },
        [loginToken]
    );

    useEffect(
        function () {
            if (userSettings?.reset === true) {
                getUserSettings(true);
            }
        },
        [userSettings]
    );

    const routes = useRoutes(routesAhdCars(!checkUndefinedNullOrEmpty(loginToken) ? getSession() : undefined));

    return (
        <>
            {loc?.state?.errorLogout ? (
                <DialogBox
                    dialogData={{
                        error: true,
                        title: 'Session abgelaufen!',
                        message: 'Sie werden in wenigen Sekunden automatisch abgemeldet.\nUm fortzufahren, melden Sie sich erneut an!',
                        autoFunc: () => {
                            dispatch(setLoginToken(''));
                            setVertretungSeit(undefined);
                            setVertretungUser(undefined);
                            clearSession();
                            betterUseNavigate(navigate, loc?.state, { replace: true, pathname: '/loginr' }, false);
                        },
                        autoTimeout: 5000,
                    }}
                />
            ) : undefined}
            {!checkUndefinedNullOrEmpty(routes) ? <Suspense fallback={<Loading>Einen Moment bitte...</Loading>}>{routes}</Suspense> : <Loading>Einen Moment bitte...</Loading>}
        </>
    );
};

export default EnsureLoggedInContainer;
